import { useState } from 'react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Form } from '@/components/ui/form';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import Textarea from '@/components/form/Textarea';
import SpinnerButton from '@/components/common/SpinnerButton';
import Error from '@/components/common/Error';

import { useCoachActivity, useRejectCoachActivity } from '@/data/coachActivity';

const formSchema = z.object({
  rejectionMessage: z.string().min(1, 'Rejection message is required')
});

const RejectDialog = ({ coachActivityId, setIsOpen }) => {
  const coachActivity = useCoachActivity(coachActivityId);
  const [apiError, setApiError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const rejectCoachActivity = useRejectCoachActivity();
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      rejectionMessage: ''
    }
  });

  const onSubmit = async (values) => {
    setSubmitting(true);
    try {
      setApiError('');
      const updatedCoachActivity = await rejectCoachActivity(
        coachActivityId,
        values
      );
      coachActivity.mutate(updatedCoachActivity);
      setSubmitting(false);
      setIsOpen(false);
    } catch (err) {
      setApiError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={setIsOpen}>
      <DialogContent className="w-[600px]">
        <DialogHeader>
          <DialogTitle>Reject Coach Activity</DialogTitle>
          <DialogDescription>
            Please provide a reason for rejecting this coach activity. The coach
            will recieve this message and be able to update accordingly.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <Textarea
              name="rejectionMessage"
              label="Rejection Message"
              height={150}
            />
            {apiError && <Error error={apiError} />}
            <DialogFooter>
              <SpinnerButton loading={submitting}>
                Reject Activity
              </SpinnerButton>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default RejectDialog;
